import { useMemo, useState, type ReactNode, type CSSProperties } from 'react';
import { Space, Tag, Dropdown, Button, Tooltip } from 'antd';
import type { MenuProps, DropdownProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { statusLabelMap, statusTagColors } from '../constants';
import { transitionOptions, type LeadTransitionAction } from '../leadTransitions';

function isPromiseLike<T = unknown>(value: unknown): value is PromiseLike<T> {
  return typeof value === 'object' && value !== null && typeof (value as PromiseLike<T>).then === 'function';
}

export type PipelineStatusMenuProps = {
  status: string;
  disabled?: boolean;
  actionsOverride?: LeadTransitionAction[];
  onActionSelected?: (action: LeadTransitionAction) => Promise<void> | void;
  trigger?: DropdownProps['trigger'];
  hideStatusTag?: boolean;
};

export function PipelineStatusMenu({
  status,
  disabled,
  actionsOverride,
  onActionSelected,
  trigger = ['click'],
  hideStatusTag = false
}: PipelineStatusMenuProps) {
  const [menuLoading, setMenuLoading] = useState(false);
  const actions: LeadTransitionAction[] = useMemo(
    () => actionsOverride ?? (transitionOptions[status as keyof typeof transitionOptions] ?? []),
    [actionsOverride, status]
  );
  const statusLabel = statusLabelMap.get(status) ?? status;
  const statusColor = statusTagColors[status] ?? 'default';

  const menuItems: MenuProps['items'] = actions.map((action) => ({
    key: action.to,
    label: action.label,
    danger: action.danger
  }));

  const handleMenuClick: MenuProps['onClick'] = async ({ key }) => {
    if (!onActionSelected || disabled) {
      return;
    }
    const action = actions.find((candidate) => candidate.to === key);
    if (!action) {
      return;
    }
    const result = onActionSelected(action);
    if (isPromiseLike(result)) {
      setMenuLoading(true);
      try {
        await result;
      } finally {
        setMenuLoading(false);
      }
    }
  };

  return (
    <Space size={8} wrap>
      {hideStatusTag ? null : <Tag color={statusColor}>{statusLabel}</Tag>}
      {menuItems.length > 0 ? (
        <Dropdown
          trigger={trigger}
          menu={{ items: menuItems, onClick: handleMenuClick }}
          disabled={disabled || menuLoading}
        >
          <Button size="small" icon={<DownOutlined />} loading={menuLoading} disabled={disabled || menuLoading}>
            Næste trin
          </Button>
        </Dropdown>
      ) : (
        <Button size="small" disabled>
          Ingen trin
        </Button>
      )}
    </Space>
  );
}

export type PipelineActionButtonsProps = {
  actions: LeadTransitionAction[];
  cancelLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  onAction: (action: LeadTransitionAction) => void;
  getActionTooltip?: (action: LeadTransitionAction) => string | null;
  isActionDisabled?: (action: LeadTransitionAction) => boolean;
  primaryExtras?: ReactNode;
  secondaryExtras?: ReactNode;
  className?: string;
  style?: CSSProperties;
};

export function PipelineActionButtons({
  actions,
  cancelLabel = 'Ingen kontrakt',
  loading,
  disabled,
  onAction,
  getActionTooltip,
  isActionDisabled,
  primaryExtras,
  secondaryExtras,
  className,
  style
}: PipelineActionButtonsProps) {
  const { regularActions, cancelActions } = useMemo(() => {
    const grouped = { regularActions: [] as LeadTransitionAction[], cancelActions: [] as LeadTransitionAction[] };
    for (const action of actions) {
      if (action.label === cancelLabel) {
        grouped.cancelActions.push(action);
      } else {
        grouped.regularActions.push(action);
      }
    }
    return grouped;
  }, [actions, cancelLabel]);

  const renderButton = (action: LeadTransitionAction) => {
    const blocked = isActionDisabled?.(action) ?? false;
    const tooltip = getActionTooltip?.(action) ?? null;
    const button = (
      <Button
        key={action.to}
        type={action.type ?? 'default'}
        danger={action.danger}
        loading={loading}
        disabled={disabled || blocked}
        onClick={() => onAction(action)}
      >
        {action.label}
      </Button>
    );
    return tooltip ? (
      <Tooltip key={`${action.to}-tooltip`} title={tooltip}>
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  return (
    <div className={className} style={style}>
      <Space wrap size="small">
        {regularActions.map((action) => renderButton(action))}
        {primaryExtras}
      </Space>
      {cancelActions.length > 0 || secondaryExtras ? (
        <Space wrap size="small" style={{ marginLeft: 'auto', justifyContent: 'flex-end' }}>
          {cancelActions.map((action) => renderButton(action))}
          {secondaryExtras}
        </Space>
      ) : null}
    </div>
  );
}
